import { NamedElementsRequestTypes } from './types';
import { NamedElementsActions } from './actions';
import { NamedElementRecord, Suggestion } from '@fullstory/proto/fs/services/search-settings';
import { AppBackendActions, AppBackendRequestTypes } from '../appbackend';
import { createRequestReducer } from '@fullstory/redux-util';
import { combineReducers } from 'redux';
import { GetAllNamedElementsResponse, ObjectDeps } from '@fullstory/proto/fs/services/dx-objects';

type State = Map<string, NamedElementRecord>;

const depsToState = (deps: ObjectDeps | null, state: State): State => {
  if (!deps) return state;
  const newState: State = new Map(state);
  const namedElements = deps.namedElements;

  for (const [namedElementId, namedElement] of Object.entries(namedElements)) {
    if (namedElement.namedElement) {
      newState.set(namedElementId, namedElement.namedElement);
    }
  }

  return newState;
};

function namedElements(
  state: State = new Map<string, NamedElementRecord>(),
  action: NamedElementsActions | AppBackendActions,
): State {
  switch (action.type) {
    case NamedElementsRequestTypes.GetAllNamedElementsSuccess: {
      const newState = new Map(state);
      action.data.namedElements.forEach(namedElement => {
        if (namedElement.namedElement?.namedElement?.id) {
          newState.set(namedElement.namedElement?.namedElement.id, namedElement.namedElement);
        }
      });
      return newState;
    }
    case NamedElementsRequestTypes.SetNamedElementSuccess: {
      const newState = new Map(state);
      if (action.data.namedElement?.namedElement?.namedElement?.id) {
        newState.set(
          action.data.namedElement.namedElement.namedElement.id,
          action.data.namedElement.namedElement,
        );
      }
      return newState;
    }
    case NamedElementsRequestTypes.ArchiveNamedElementSuccess: {
      const newState = new Map(state);
      if (action.data.namedElement?.namedElement?.namedElement?.id) {
        newState.set(
          action.data.namedElement.namedElement.namedElement.id,
          action.data.namedElement.namedElement,
        );
      }
      return newState;
    }
    case AppBackendRequestTypes.GetSegmentDetailViewSuccess:
    case AppBackendRequestTypes.GetFunnelDetailViewSuccess:
    case AppBackendRequestTypes.GetSidebarSuccess: {
      return depsToState(action.data.deps, state);
    }

    default:
      return state;
  }
}

type suggestedElementsState = {
  pending: Map<string, NamedElementRecord>;
  all: Map<string, NamedElementRecord>;
};

function suggestedElements(
  state: suggestedElementsState = { pending: new Map(), all: new Map() },
  action: NamedElementsActions | AppBackendActions,
): suggestedElementsState {
  switch (action.type) {
    case NamedElementsRequestTypes.GetAllNamedElementsSuccess: {
      const newState: suggestedElementsState = {
        pending: new Map(state.pending),
        all: new Map(state.all),
      };
      action.data.pendingSuggestedElements.forEach(namedElement => {
        const suggestion = namedElement.namedElement?.namedElement?.suggestion;
        if (suggestion && suggestion.id) {
          newState.all.set(suggestion.id, namedElement.namedElement!);
          if (suggestion.status === Suggestion.Status.STATUS_PENDING) {
            newState.pending.set(suggestion.id, namedElement.namedElement!);
          }
        }
      });
      return newState;
    }
    case NamedElementsRequestTypes.SetSuggestedNamedElementSuccess: {
      const newState = {
        ...state,
        pending: new Map(state.pending),
      };
      const suggestion = action.data.suggestion?.suggestion?.suggestion;
      if (suggestion?.id && suggestion.status !== Suggestion.Status.STATUS_PENDING) {
        newState.pending.delete(suggestion.id);
      }
      return newState;
    }
    default:
      return state;
  }
}

export const getAllNamedElements = createRequestReducer<GetAllNamedElementsResponse>()(
  NamedElementsRequestTypes.GetAllNamedElementsRequest,
  NamedElementsRequestTypes.GetAllNamedElementsSuccess,
  NamedElementsRequestTypes.GetAllNamedElementsFailure,
);

type NamedElementsReducerState = {
  namedElements: ReturnType<typeof namedElements>;
  suggestedElements: ReturnType<typeof suggestedElements>;
  getAllNamedElements: ReturnType<typeof getAllNamedElements>;
};

export const namedElementsReducer = combineReducers<NamedElementsReducerState>({
  namedElements,
  suggestedElements,
  getAllNamedElements,
});
